<script setup lang="ts">
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { emailValidator, requiredValidator } from "@validators";
import { VForm } from "vuetify/components";
import type { BookingStepProps } from "../types/index";
import {isEmpty} from "@core/utils";
const props = defineProps<BookingStepProps>();
import { moment } from "@/utils/useTimeZone";

const emit = defineEmits<{
  (e: "update:form", value: any): void;
  (e: "validate"): Promise<boolean>;
  (e: "updateErrorMessage", value: any): void;
}>();

const membersFormRef = ref<InstanceType<typeof VForm>>();
const userStore = useUserStore();
const { currentLocation, currentLocationDetails } = storeToRefs(userStore);

const selectedActivity = computed(() =>
  props.activityList?.find((el) => el.id === props.form.activity)
);

const productList = computed(() => {
  return selectedActivity.value?.expand?.options.filter(
    (option: any) => option.location === currentLocation.value
  );
});

const errorMessage = ref("");

const checkItemAvailability = async (optionId: string) => {
  const option = selectedActivity.value?.expand?.options?.find(
    (opt: any) => opt.id === optionId
  );
  if (!option) {
    return false;
  }
  if (option?.inStock <= 0) {
    emit("updateErrorMessage", {
      isVisible: true,
      message: "Sorry out of stock on this item",
    });
    return false;
  }

  if (option?.hiredOutItem) {
    const bookingStart = moment
      .tz(
        `${props.form.date} ${props.form.time}`,
        "DD-MM-YYYY HH:mm",
        currentLocationDetails.value?.timeZoneCode
      )
      .add(1, "seconds");
    const bookingSpacing = selectedActivity.value?.bookingSpacing || 60; // Default to 60 minutes if due is not set
    const bookingEnd = bookingStart
      .clone()
      .add(Number(props.form.duration || 1) * bookingSpacing, "minutes")
      .add(-1, "seconds");
    try {
      const bookings = await pb.collection("bookings").getList(1, 50, {
        filter: `startTime < '${bookingEnd
          .utc()
          .format("YYYY-MM-DD HH:mm:ss")}' && endTime > '${bookingStart
          .utc()
          .format("YYYY-MM-DD HH:mm:ss")}'`,
        expand: "docketLine",
      });

      let bookedCount = 0;
      for (const booking of bookings.items) {
        if (booking.options?.length) {
          if (booking.options.includes(optionId)) {
            bookedCount += 1;
          }
        }
      }

      if (bookedCount >= option.inStock) {
        emit("updateErrorMessage", {
          isVisible: true,
          message: "Hire Item not available for the booking time selected",
        });
        return false;
      }
    } catch (error) {
      console.error("Error checking availability:", error);
      emit("updateErrorMessage", {
        isVisible: true,
        message: errorMessage.value,
      });
      return false;
    }
  }
  return true;
};

onMounted(() => {
  membersFormRef.value?.validate();
});
defineExpose({
  validate: async () => {
    const results = await membersFormRef.value?.validate();

    return results?.valid ?? false;
  },
});

const LocalEmailValidator = (value: unknown) => {
  if (isEmpty(value)) return currentLocationDetails.value?.guestValidateEmail ? 'You must provide a valid email' : true;
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  if (Array.isArray(value))
    return (
      value.every((val) => re.test(String(val))) ||
      "The Email field must be a valid email"
    );
  return re.test(String(value)) || "The Email field must be a valid email";
};

const LocalMobileValidator = (value: unknown) => {
  if (isEmpty(value)) return currentLocationDetails.value?.guestValidateSMS ? 'You must provide a valid Mobile Number' : true;

  value = value.replace(/\D/g,'');
  const re = /^([0|\+[0-9]{1,5})?([0-9]{10})$/;
  if (Array.isArray(value))
    return (
      value.every((val) => re.test(String(val))) ||
      "This must be a valid mobile number"
    );
  return re.test(String(value)) || "This must be a valid mobile number";
};

const canHire = ref(false)

onMounted(async ()=>{
  canHire.value = false
  if(props?.form?.activity && props?.form?.time && props?.form?.date){
    // Get Activity Times
    let BookingDateCheck = props?.form?.date.split("-")[2] + "-" + props?.form?.date.split("-")[1] + "-" + props?.form?.date.split("-")[0]
    let allActiveTimes:any = await pb.collection('activities').getOne(props?.form?.activity, {expand:'availibleTimes'})
    if(allActiveTimes && allActiveTimes?.expand?.availibleTimes){
      canHire.value = allActiveTimes.expand.availibleTimes.filter((at: any) => {
        if (!at?.allUsers) {
          return false
        }
        let validFrom = moment(at?.validFrom)
        let validTill = moment(at?.validTill)
        let nowTime = moment(BookingDateCheck)

        if ((at?.validFrom != '' && validFrom.isAfter(nowTime)) || (at?.validTill != '' && validTill.isBefore(nowTime))) {
          return false
        }
        let dayCheck = nowTime.format('dddd').toString().toLowerCase()

        if (!at[dayCheck]) {
          return false
        }

        let hour = parseInt(props?.form?.time || 0)

        if (hour >= at.startHour && hour <= at.finishHour) {
          return true
        }
        return false
      }).length > 0
    }
  }
})
</script>

<template>
  <VForm ref="membersFormRef">
    <VRow v-if="currentLocationDetails.value?.payInStoreEnabled || pb?.authStore?.model?.id">
      <!-- Booking Name -->
      <VCol
        lg="4"
        md="4"
        sm="4"
        xs="4"
        cols="12"
        v-if="!pb?.authStore?.model?.id"
      >
        <VTextField
          v-model="form.guests"
          label="Booking Name"
          :rules="[requiredValidator]"
          @update:modelValue="
            emit('update:form', { ...props.form, guests: [$event] })
          "
        />
      </VCol>
      <!-- Booking Email -->
      <VCol
        lg="4"
        md="4"
        sm="4"
        xs="4"
        cols="12"
        v-if="!pb?.authStore?.model?.id"
      >
        <VTextField
          v-model="form.visitorEmail"
          label="Email"
          :rules="[emailValidator]"
          placeholder="Please enter your email."
          @update:modelValue="
            emit('update:form', { ...props.form, visitorEmail: $event })
          "
        />
      </VCol>
      <!-- Booking Phone -->
      <VCol
        lg="4"
        md="4"
        sm="4"
        xs="4"
        cols="12"
        v-if="!pb?.authStore?.model?.id"
      >
        <VTextField
          v-model="form.visitorPhone"
          label="Phone"
          placeholder="Please enter a contact number."
          @update:modelValue="
            emit('update:form', { ...props.form, visitorPhone: $event })
          "
        />
      </VCol>
      </VRow>
    <VRow v-else>
      <!-- Booking Name -->
      <VCol
        cols="12">
        <VTextField
          v-model="form.guests"
          label="Booking Name"
          :rules="[requiredValidator]"
          @update:modelValue="emit('update:form', { ...props.form, guests: [$event] })"/>

        <v-alert color="info" class="mt-5"
                 v-if="currentLocationDetails?.guestValidateSMS || currentLocationDetails?.guestValidateEmail">
          <v-icon icon="mdi-warning"></v-icon> To gain Access to the building, you must provide a valid
          <span v-if="currentLocationDetails?.guestValidateEmail"> Email Address </span>
          <span v-if="currentLocationDetails?.guestValidateSMS"><span v-if="currentLocationDetails?.guestValidateEmail">and</span> Mobile Number</span></v-alert>
      </VCol>

      <!-- Booking Email -->
      <VCol
        cols="6">
        <VTextField
          v-model="form.visitorEmail"
          label="Email"
          :rules="[LocalEmailValidator]"
          placeholder="Please enter your email."
          @update:modelValue="
            emit('update:form', { ...props.form, visitorEmail: $event })
          "
        />
      </VCol>
      <!-- Booking Phone -->
      <VCol
        cols="6">
        <VTextField
          v-model="form.visitorPhone"
          label="Phone"
          :rules="[LocalMobileValidator]"
          placeholder="Please enter a contact number."
          @keyup="form.visitorPhone = form.visitorPhone.replace(/\D/g,'')"
          @update:modelValue="
            emit('update:form', { ...props.form, visitorPhone : $event })
          "
        />
      </VCol>

    </VRow>


      <VRow>
      <!-- Activity Options -->
      <VCol cols="12" v-if="productList?.length && canHire">

        <div>
          <div class="text-subtitle-1 mb-2">Available Extras:</div>
          <div class="d-flex flex-wrap gap-4">
            <VCheckbox
              v-for="option in productList"
              :key="option.id"
              :label="option.name"
              :value="option.id"
              v-model="form.selectedOptions"
              color="primary"
              density="compact"
              @update:modelValue="
                async (newVal: any) => {
                  if (newVal.includes(option.id)) {
                    const isAvailable = await checkItemAvailability(option.id);
                    if (!isAvailable) {
                      form.selectedOptions = form?.selectedOptions?.filter(
                        (id: any) => id !== option.id
                      );
                      return;
                    }
                  }
                  emit('update:form', {
                    ...props.form,
                    selectedOptions: form.selectedOptions,
                  });
                }
              "
            />
          </div>
        </div>
      </VCol>

      <!-- Notes -->
      <VCol cols="12">
        <VTextarea
          v-model="form.notes"
          label="Booking Notes"
          @update:modelValue="$emit('update:form', { ...form, notes: $event })"
        />
      </VCol>
    </VRow>
  </VForm>
</template>
<style scoped>
.required-field {
  color: red;
  font-size: 12px;
  margin-block-end: -20px;
}
</style>
